// When the tenant's theme is applied (E12-S1-T4). The page is not shown until a theme is on <html>
// (src/styles/app.css), so this decides the moment the first frame may paint, and with what:
//
//   checking      nothing yet: the session check is the only request (E12-S1-T2)
//   signed out    the default theme — branding is the tenant's, and there is no tenant yet
//   failed        the default theme, so the failure can be read
//   signed in     GET /v1/branding, then that theme; 404 is no branding (the default, quietly);
//                 any other failure, or no answer within the deadline, is the default and a report
//
// The theme is applied once, in a layout effect, and the name and logo follow in the same frame
// (a state update in a layout effect renders again before the browser paints). It never changes
// for the life of the page: sign-in and sign-out are top-level navigations, which load it afresh.
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isApiError, ValidationError } from "../api/errors.js";
import { useApi } from "../api/provider.js";
import { useSession } from "../auth/session.js";
import {
  applyTenantTheme,
  Branding,
  brandingPath,
  DEFAULT_TENANT_THEME,
  warnTheme,
  type ReportTheme,
  type TenantTheme,
} from "./tenant-theme.js";

/** How long the page waits for the session and the branding before it shows the default theme. */
export const THEME_DEADLINE_MS = 3000;

export const brandingQueryKey = ["v1", "branding"] as const;

const ThemeContext = createContext<TenantTheme>(DEFAULT_TENANT_THEME);

/** The theme this page was drawn with: the tenant's name and logo, or the default's. */
export function useTenantTheme(): TenantTheme {
  return useContext(ThemeContext);
}

/**
 * The organisation's display name, when the tenant's branding supplies one; otherwise undefined. A
 * Principal's orgId is a minted id and is never a name, so it is never a fallback.
 */
export function useOrgName(): string | undefined {
  return useContext(ThemeContext).productName;
}

export interface ThemeGateProps {
  /** Where the theme goes: the document element of this window. */
  readonly root?: HTMLElement | undefined;
  readonly report?: ReportTheme | undefined;
  readonly deadlineMs?: number | undefined;
  readonly children: ReactNode;
}

/** The branding to apply, once it is known; undefined while it is not. */
type Decision = { readonly branding: Branding | undefined } | undefined;

export function ThemeGate({
  root,
  report = warnTheme,
  deadlineMs = THEME_DEADLINE_MS,
  children,
}: ThemeGateProps) {
  const api = useApi();
  const session = useSession();
  const signedIn = session.status === "signed-in";
  const branding = useQuery({
    queryKey: brandingQueryKey,
    queryFn: () => api.get(brandingPath, Branding),
    enabled: signedIn,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [theme, setTheme] = useState<TenantTheme | undefined>(undefined);
  const applied = useRef(false);
  const target = root ?? document.documentElement;

  let decision: Decision;
  if (session.status === "signed-out" || session.status === "failed")
    decision = { branding: undefined };
  else if (signedIn && branding.isSuccess) decision = { branding: branding.data };
  else if (signedIn && branding.isError) decision = { branding: undefined };

  const failure = signedIn && branding.isError ? branding.error : undefined;

  useLayoutEffect(() => {
    if (applied.current || decision === undefined) return;
    applied.current = true;
    if (failure !== undefined) {
      if (!isApiError(failure)) report({ code: "branding.unavailable", reason: "server" });
      else if (failure instanceof ValidationError)
        report({ code: "branding.invalid", issues: failure.issues.map((i) => i.path || "(root)") });
      else if (failure.kind !== "not-found" && failure.kind !== "unauthorised")
        report({ code: "branding.unavailable", reason: failure.kind });
    }
    setTheme(applyTenantTheme(target, decision.branding, report));
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (applied.current) return;
      applied.current = true;
      report({ code: "branding.unavailable", reason: "timeout" });
      setTheme(applyTenantTheme(target, undefined, report));
    }, deadlineMs);
    return () => {
      clearTimeout(timer);
    };
  }, [target, report, deadlineMs]);

  return <ThemeContext value={theme ?? DEFAULT_TENANT_THEME}>{children}</ThemeContext>;
}
